'use client';

import React from 'react';
import Image from 'next/image';
import { ThumbsDown, ThumbsUp } from 'lucide-react';

const ComparisonSection = () => {
  return (
    <section id="comparison" className="py-16 px-4 md:px-8 bg-primary-50 scroll-mt-12">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6 font-funnel">
          La <span className="text-accent">precisione</span> che puoi verificare
        </h2>
        <p className="text-lg text-neutral-700 mb-2">
          Usare AI generaliste in ambito legale e fiscale può esporti a informazioni errate, fonti
          inventate e dati obsoleti, con conseguenze gravi per la tua attività e la tua reputazione.
        </p>
        <p className="text-lg text-neutral-700 mb-12">
          Trovanorma nasce per eliminare questi rischi: ogni risposta è fondata su fonti ufficiali
          verificate e aggiornate in tempo reale.
        </p>
        <div className="flex flex-col text-gray-300 overflow-hidden md:bg-white rounded-none md:rounded-xl border-0 md:border-1 border-primary-100 shadow-none md:shadow-md md:hover:shadow-lg transition duration-200">
          <div className="min-w-full align-middle overflow-x-auto">
            {/* Layout flessibile responsivo che sostituisce la tabella */}
            <div className="min-w-full">
              {/* Intestazioni visibili solo su desktop */}
              <div className="hidden md:flex w-full border-b border-neutral-200 bg-white">
                <div className="flex-1 py-3.5 pl-6 pr-3 text-center text-sm font-semibold text-text-base w-1/3"></div>
                <div className="flex-2 px-3 py-3.5 text-center text-md font-semibold text-text-base w-1/3">
                  ChatGPT
                </div>
                <div className="flex-2 px-3 py-3.5 text-center text-md font-semibold text-text-base w-1/3">
                  <Image
                    src="/images/logo-horizontal-orange-blue.svg"
                    alt="Trovanorma"
                    width={140}
                    height={24}
                    className="inline-block"
                  />
                </div>
              </div>

              {/* Corpo: card responsivi per ogni elemento */}
              <div>
                {comparisonData.map(item => (
                  <div
                    key={item.criterion}
                    className="flex flex-col md:flex-row justify-start bg-white md:bg-transparent rounded-xl shadow-sm border border-gray-200 md:shadow-none md:border-none md:border-b md:rounded-none mb-2 md:mb-0"
                  >
                    {/* Criterio */}
                    <div className="flex-1 py-4 pl-6 pr-3 text-base md:text-sm font-semibold text-text-base md:w-1/3 content-center border-b border-neutral-200">
                      {item.criterion}
                    </div>

                    {/* AI Generalista */}
                    <div className="flex-2 px-4 py-4 text-sm text-neutral-600 md:w-1/3 border-l content-center border-b border-neutral-200">
                      <div className="flex items-center gap-2">
                        <ThumbsDown className="text-danger-500 flex-shrink-0 mr-2" size={20} />
                        <span className="text-left">
                          <span className="font-bold text-danger-500 md:hidden">ChatGPT: </span>
                          {item.generalAI}
                        </span>
                      </div>
                    </div>

                    {/* Trovanorma */}
                    <div className="flex-2 px-4 py-4 text-sm text-neutral-600 md:w-1/3 bg-success-500/8 rounded-b-xl md:rounded-none content-center md:border-b border-neutral-200">
                      <div className="flex items-center gap-2">
                        <ThumbsUp className="text-success-500 flex-shrink-0 mr-2" size={20} />
                        <span className="text-left">
                          <span className="text-base font-bold font-funnel text-success-500 md:hidden">
                            Trovanorma:{' '}
                          </span>
                          {item.trovanorma}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const comparisonData = [
  {
    criterion: 'Fonte dei Dati',
    generalAI: (
      <>
        <strong>Non verificata e generica.</strong> Le informazioni provengono dal web e possono
        essere ferme a mesi o anni fa, senza garanzia di accuratezza o pertinenza legale.
      </>
    ),
    trovanorma: (
      <>
        <strong>Database proprietario e verificato.</strong> Include l’intero corpus normativo e
        giurisprudenziale italiano ed europeo, proveniente da Gazzetta Ufficiale, codici e sentenze.
      </>
    ),
  },
  {
    criterion: 'Aggiornamento dei Dati',
    generalAI: (
      <>
        <strong>Obsoleto o assente.</strong> Non recepisce in tempo reale modifiche normative o
        giurisprudenziali cruciali.
      </>
    ),
    trovanorma: (
      <>
        <strong>Sempre aggiornato.</strong> Sincronizzazione quotidiana con le fonti ufficiali per
        garantirti le informazioni più recenti.
      </>
    ),
  },
  {
    criterion: 'Citazione delle Fonti',
    generalAI: (
      <>
        <strong>Assente o inaffidabile.</strong> Fonti errate, incomplete o non funzionanti che
        rendono impossibile la verifica.
      </>
    ),
    trovanorma: (
      <>
        <strong>Obbligatoria e precisa.</strong> Ogni risposta ha riferimenti diretti e link alla
        norma o sentenza originale.
      </>
    ),
  },
  {
    criterion: 'Specializzazione',
    generalAI: (
      <>
        <strong>Uso generico.</strong> Non comprende la terminologia e la logica giuridico-fiscale,
        aumentando il rischio di errori di interpretazione.
      </>
    ),
    trovanorma: (
      <>
        <strong>Addestrato per il diritto.</strong> Linguaggio specialistico e analisi pertinenti,
        con contesto e correlazioni corrette.
      </>
    ),
  },
];

export default ComparisonSection;
